import { createApi } from "@reduxjs/toolkit/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { mapWithConcurrency } from "../../utils/concurrency";

/**
 * オファー（契約）に紐づかない、ステータスを持たないシンプルなグループ DM
 * スレッドの API クライアント。既存の `dmThreadsApi`（契約スレッド）とは
 * 別ドメインで、`/api/me/<project_id>/chats/` を叩く。
 */

export type ChatMemberPosition =
  | "CLIENT"
  | "REVIEWER"
  | "ADMIN"
  | "PRODUCER"
  | "SPECIALIST"
  | "DIRECTOR";

export interface ChatUser {
  id: number;
  display_name: string;
  avatar: string | null;
  position: ChatMemberPosition | null;
  /** receivers 配列の要素のみ持つ既読時刻 */
  seen_at?: string;
}

export interface ChatThreadLastMessage {
  id: string;
  message: string;
  author: ChatUser | null;
  created_at: string;
}

export interface ChatThread {
  id: string;
  title: string | null;
  created_by: ChatUser | null;
  members: ChatUser[];
  last_message: ChatThreadLastMessage | null;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface ChatThreadsData {
  threads: ChatThread[];
  count: number;
  has_next: boolean;
  next_offset: number | null;
  limit: number;
  offset: number;
}

export interface ChatThreadsResponse {
  status: string;
  data: ChatThreadsData;
}

export interface ChatThreadDetailResponse {
  status: string;
  data: { thread: ChatThread };
}

export interface GetChatThreadsParams {
  project_id: string;
  limit?: number;
  offset?: number;
}

export interface GetChatThreadDetailParams {
  project_id: string;
  chat_id: string;
}

export interface CreateChatThreadParams {
  project_id: string;
  /** 相手先ユーザー ID（自分以外。複数選択でグループ DM になる） */
  member_user_ids: number[];
  title?: string;
}

export interface CreateChatThreadResponse {
  status: string;
  data: { thread: ChatThread };
}

export interface DeleteChatThreadParams {
  project_id: string;
  chat_id: string;
}

/** 1 リクエストあたりの取得件数。BE の `ChatThreadPagination.max_limit` と同値。 */
const CHAT_THREADS_PAGE_LIMIT = 100;
/** 全ページ取得の安全上限（無限ループ防止。100 件 × 20 ページ = 2000 件） */
const CHAT_THREADS_MAX_PAGES = 20;
/** 2 ページ目以降をまとめて取りに行くときの同時リクエスト数 */
const CHAT_THREADS_FETCH_CONCURRENCY = 4;

/** ページ指定から Chat スレッド一覧 API の URL を組み立てる。 */
export const buildChatThreadsUrl = (
  { project_id }: GetChatThreadsParams,
  page: { limit?: number; offset?: number } = {},
): string => {
  const params: Record<string, string> = {};
  if (page.limit !== undefined) params.limit = page.limit.toString();
  if (page.offset !== undefined) params.offset = page.offset.toString();
  const queryString = new URLSearchParams(params).toString();
  const qs = queryString ? `?${queryString}` : "";
  return `/api/me/${project_id}/chats/${qs}`;
};

/** `queryFn` の `fetchWithBQ` のうち、ページ取得で使う部分だけを写した型。 */
type PageResult = { data?: unknown; error?: FetchBaseQueryError };
type FetchPage = (url: string) => PageResult | PromiseLike<PageResult>;

type ChatThreadsQueryResult =
  | { data: ChatThreadsResponse; error?: undefined }
  | { data?: undefined; error: FetchBaseQueryError };

/**
 * Chat スレッド一覧を全ページ取得して 1 つのレスポンスに畳む。
 *
 * BE は既定 50 件・最大 100 件でページネーションする。DM の左カラムは契約スレッドと
 * マージして表示し、絞り込みもクライアント側で行うため、`dmThreadsApi` と同じく
 * ここで全件を揃えてから返す。
 */
export const fetchAllChatThreadPages = async (
  args: GetChatThreadsParams,
  fetchPage: FetchPage,
): Promise<ChatThreadsQueryResult> => {
  const pageLimit = args.limit ?? CHAT_THREADS_PAGE_LIMIT;
  const getPage = (offset: number) =>
    fetchPage(buildChatThreadsUrl(args, { limit: pageLimit, offset }));

  const firstResult = await getPage(0);
  if (firstResult.error) return { error: firstResult.error };

  const firstData = (firstResult.data as ChatThreadsResponse).data;
  const allThreads: ChatThread[] = [...(firstData.threads || [])];

  // BE が limit をクランプした場合にも追従できるよう、応答の limit を使う。
  const effectiveLimit = firstData.limit || pageLimit;
  const totalPages = Math.min(
    Math.ceil((firstData.count || allThreads.length) / effectiveLimit),
    CHAT_THREADS_MAX_PAGES,
  );
  const restOffsets = Array.from(
    { length: Math.max(0, totalPages - 1) },
    (_, index) => (index + 1) * effectiveLimit,
  );

  const results = await mapWithConcurrency(
    restOffsets,
    CHAT_THREADS_FETCH_CONCURRENCY,
    (offset) => Promise.resolve(getPage(offset)),
  );
  for (const result of results) {
    if (result.error) return { error: result.error };
    allThreads.push(
      ...((result.data as ChatThreadsResponse).data.threads || []),
    );
  }

  // 全件返したときは呼び出し側が誤って追加取得しないよう潰す。
  // `CHAT_THREADS_MAX_PAGES` で打ち切ったときだけ続きがあることを残す。
  const nextOffset = totalPages * effectiveLimit;
  const isTruncated = nextOffset < (firstData.count || allThreads.length);

  return {
    data: {
      status: "success",
      data: {
        ...firstData,
        threads: allThreads,
        has_next: isTruncated,
        next_offset: isTruncated ? nextOffset : null,
        limit: effectiveLimit,
        offset: 0,
      },
    },
  };
};

export const chatThreadsApi = createApi({
  reducerPath: "chatThreadsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["ChatThreads", "ChatThreadDetail"],
  endpoints: (builder) => ({
    getChatThreads: builder.query<ChatThreadsResponse, GetChatThreadsParams>({
      queryFn: async (args, _api, _extraOptions, fetchWithBQ) => {
        // limit / offset の明示は呼び出し側がページサイズを決めている意図なので
        // （`useProjectDmAccess` の件数判定など）、そのまま 1 ページだけ返す。
        if (args.limit !== undefined || args.offset !== undefined) {
          const result = await fetchWithBQ(
            buildChatThreadsUrl(args, {
              limit: args.limit,
              offset: args.offset,
            }),
          );
          if (result.error) return { error: result.error };
          return { data: result.data as ChatThreadsResponse };
        }
        return fetchAllChatThreadPages(args, fetchWithBQ);
      },
      providesTags: ["ChatThreads"],
      keepUnusedDataFor: 30,
    }),

    // 現状 FE では未使用（右カラムは一覧 getChatThreads の結果から描画する）。
    // 将来の詳細再取得・ディープリンク検証用に BE の GET /chats/<id> へ対応させてある。
    getChatThreadDetail: builder.query<
      ChatThreadDetailResponse,
      GetChatThreadDetailParams
    >({
      query: ({ project_id, chat_id }) =>
        `/api/me/${project_id}/chats/${chat_id}`,
      providesTags: (_result, _error, arg) => [
        { type: "ChatThreadDetail", id: arg.chat_id },
      ],
      keepUnusedDataFor: 60,
    }),

    /** グループ DM スレッドを新規作成する（作成者は自動で参加者に含まれる）。 */
    createChatThread: builder.mutation<
      CreateChatThreadResponse,
      CreateChatThreadParams
    >({
      query: ({ project_id, member_user_ids, title }) => ({
        url: `/api/me/${project_id}/chats/`,
        method: "POST",
        body: {
          member_user_ids,
          ...(title !== undefined && title !== "" && { title }),
        },
      }),
      invalidatesTags: ["ChatThreads"],
    }),

    /**
     * DM スレッドから退出する（残り 0 人で論理削除）。
     * 現状 FE には退出 UI が無いため未使用。BE の DELETE /chats/<id> 対応として用意。
     */
    deleteChatThread: builder.mutation<void, DeleteChatThreadParams>({
      query: ({ project_id, chat_id }) => ({
        url: `/api/me/${project_id}/chats/${chat_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        "ChatThreads",
        { type: "ChatThreadDetail", id: arg.chat_id },
      ],
    }),
  }),
});

export const {
  useGetChatThreadsQuery,
  useGetChatThreadDetailQuery,
  useCreateChatThreadMutation,
  useDeleteChatThreadMutation,
} = chatThreadsApi;

/**
 * Chat スレッドの表示名（自分以外の参加者名を「、」で連結）を返す。
 * 参加者が自分だけ / 不明な場合は title または "DM" にフォールバックする。
 * ChatThreadItem（一覧行）と ChatMessenger（ヘッダー）で共通利用する。
 */
export const getChatThreadDisplayName = (
  thread: Pick<ChatThread, "members" | "title">,
  currentUserId?: number,
): string => {
  const others = thread.members.filter((m) => m.id !== currentUserId);
  if (others.length === 0) return thread.title || "DM";
  return others.map((m) => m.display_name).join("、");
};
